/**
 * Bundle builder — custom TANIT Box widget.
 *
 * Keeps Recharge's dynamic-bundle cart contract: each selected product is added
 * as its own cart line carrying
 *   _rc_bundle               "<9-char id>:<bundle product id>"
 *   _rc_bundle_parent        bundle product handle
 *   _rc_bundle_variant       bundle product variant id
 *   _rc_bundle_collection_id collection the item was picked from
 * plus the child's selling plan matching the chosen delivery frequency.
 * Recharge's tiered "BUY MORE, SAVE MORE" discount is applied at checkout by
 * Recharge itself, exactly as with the native widget.
 */
(function () {
  'use strict';

  var NANO_ALPHABET = 'useandom-26T198340PX75pxJACKVERYMINDBUSHWOLF_GQZbfghjklqvwyzrict';

  function nanoid(size) {
    var id = '';
    var bytes = new Uint8Array(size);
    if (window.crypto && window.crypto.getRandomValues) {
      window.crypto.getRandomValues(bytes);
    } else {
      for (var i = 0; i < size; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    for (var j = 0; j < size; j++) id += NANO_ALPHABET[bytes[j] & 63];
    return id;
  }

  // Funnel analytics: every event goes to Shopify's customer-events bus (picked up by a custom pixel
  // that forwards to GA4) and to window.dataLayer (for GTM/gtag if ever present on the page).
  function track(name, data) {
    var payload = data || {};
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (e) { /* ignore */ }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, payload));
    } catch (e) { /* ignore */ }
  }

  function tpl(str, vars) {
    return String(str).replace(/\[(\w+)\]/g, function (m, k) {
      return vars[k] !== undefined ? vars[k] : m;
    });
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function restartAnimation(el, className) {
    el.classList.remove(className);
    void el.offsetWidth;
    el.classList.add(className);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function formatMoney(cents, format) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      try {
        return String(theme.Currency.formatMoney(cents, format)).replace(/<[^>]*>/g, '');
      } catch (e) {
        /* fall through */
      }
    }
    var amount = (cents / 100).toFixed(2);
    if (format && format.indexOf('{{') > -1) {
      return format
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100))
        .replace(/\{\{\s*amount\s*\}\}/, amount);
    }
    return '$' + amount;
  }

  /* ---------- custom select (wraps a native <select>, which stays as the source of truth) ---------- */

  function enhanceSelect(select) {
    if (select.__bbEnhanced) return;
    select.__bbEnhanced = true;

    var wrap = document.createElement('div');
    wrap.className = 'bb-select';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bb-select__btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    if (select.getAttribute('aria-label')) btn.setAttribute('aria-label', select.getAttribute('aria-label'));
    var list = document.createElement('ul');
    list.className = 'bb-select__list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    function optionRow(opt) {
      return '<span class="bb-select__text">' + escapeHtml(opt.textContent.trim()) + '</span>';
    }

    function build() {
      list.innerHTML = '';
      Array.prototype.forEach.call(select.options, function (opt) {
        var li = document.createElement('li');
        li.className = 'bb-select__option' + (opt.disabled ? ' is-disabled' : '');
        li.setAttribute('role', 'option');
        li.setAttribute('data-value', opt.value);
        li.tabIndex = -1;
        var inBox = Number(opt.getAttribute('data-in-box')) || 0;
        li.innerHTML =
          optionRow(opt) +
          (inBox ? '<span class="bb-select__qty">\u00d7' + inBox + '</span>' : '') +
          '<span class="bb-select__check" aria-hidden="true"></span>';
        list.appendChild(li);
      });
    }

    function render() {
      var opt = select.options[select.selectedIndex];
      btn.innerHTML =
        (opt ? optionRow(opt) : '<span class="bb-select__text"></span>') +
        '<svg class="bb-select__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>';
      Array.prototype.forEach.call(list.children, function (li) {
        var selected = li.getAttribute('data-value') === select.value;
        li.setAttribute('aria-selected', selected ? 'true' : 'false');
        li.classList.toggle('is-selected', selected);
      });
    }

    function open() {
      if (!list.hidden) return;
      closeAllSelects();
      list.hidden = false;
      wrap.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
      var card = wrap.closest('.bb-card');
      if (card) card.classList.add('has-open-select');
      // Flip upwards when there is no room below.
      var rect = list.getBoundingClientRect();
      wrap.classList.toggle('is-up', rect.bottom > window.innerHeight - 8 && rect.top > rect.height + 8);
    }

    function close() {
      if (list.hidden) return;
      list.hidden = true;
      wrap.classList.remove('is-open', 'is-up');
      btn.setAttribute('aria-expanded', 'false');
      var card = wrap.closest('.bb-card');
      if (card) card.classList.remove('has-open-select');
    }

    function choose(li) {
      if (!li || li.classList.contains('is-disabled')) return;
      var value = li.getAttribute('data-value');
      if (select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      render();
      close();
      btn.focus();
    }

    function focusMove(delta) {
      var items = Array.prototype.filter.call(list.children, function (li) {
        return !li.classList.contains('is-disabled');
      });
      if (!items.length) return;
      var idx = items.indexOf(document.activeElement);
      if (idx === -1) idx = items.findIndex(function (li) { return li.classList.contains('is-selected'); });
      idx = (idx + delta + items.length) % items.length;
      items[idx].focus();
    }

    btn.addEventListener('click', function () {
      if (list.hidden) open();
      else close();
    });
    btn.addEventListener('keydown', function (evt) {
      if (evt.key === 'ArrowDown' || evt.key === 'ArrowUp') {
        evt.preventDefault();
        open();
        focusMove(evt.key === 'ArrowDown' ? 1 : -1);
      }
    });
    list.addEventListener('click', function (evt) {
      choose(evt.target.closest('.bb-select__option'));
    });
    list.addEventListener('keydown', function (evt) {
      if (evt.key === 'ArrowDown') { evt.preventDefault(); focusMove(1); }
      else if (evt.key === 'ArrowUp') { evt.preventDefault(); focusMove(-1); }
      else if (evt.key === 'Enter' || evt.key === ' ') { evt.preventDefault(); choose(document.activeElement); }
      else if (evt.key === 'Escape') { evt.preventDefault(); close(); btn.focus(); }
      else if (evt.key === 'Tab') close();
    });
    select.addEventListener('change', render);

    wrap.__close = close;
    // Rebuild the rows when the in-box counts change.
    select.__bbRefresh = function () {
      build();
      render();
    };
    wrap.appendChild(btn);
    wrap.appendChild(list);
    select.classList.add('bb-visually-hidden');
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    select.parentNode.insertBefore(wrap, select.nextSibling);
    build();
    render();
  }

  function closeAllSelects() {
    Array.prototype.forEach.call(document.querySelectorAll('.bb-select.is-open'), function (w) {
      if (w.__close) w.__close();
    });
  }

  document.addEventListener('click', function (evt) {
    if (!evt.target.closest('.bb-select')) closeAllSelects();
  });
  document.addEventListener('keydown', function (evt) {
    if (evt.key === 'Escape') closeAllSelects();
  });

  function BundleBuilder(root) {
    this.root = root;
    var cfgEl = root.querySelector('[data-bb-config]');
    if (!cfgEl) return;
    this.cfg = JSON.parse(cfgEl.textContent);
    this.cfg.tiers = (this.cfg.tiers || []).slice().sort(function (a, b) {
      return a.qty - b.qty;
    });
    this.products = {};
    this.cfg.products.forEach(function (p) {
      this.products[p.id] = p;
    }, this);

    this.items = []; // [{ productId, variantId, collectionId }]
    this.selectedChoice = {}; // cardKey -> 'productId:variantId'
    this.afterAdd = root.getAttribute('data-after-add') || 'checkout';

    this.el = {
      grid: root.querySelector('[data-grid]'),
      cards: Array.prototype.slice.call(root.querySelectorAll('[data-card]')),
      tabs: Array.prototype.slice.call(root.querySelectorAll('[data-tab].bb-tab')),
      progressLabels: root.querySelector('[data-progress-labels]'),
      progressBar: root.querySelector('[data-progress-bar]'),
      progressNext: root.querySelector('[data-progress-next]'),
      tierMsg: root.querySelector('[data-tier-msg]'),
      slots: root.querySelector('[data-slots]'),
      totals: root.querySelector('[data-totals]'),
      cta: root.querySelector('[data-cta]'),
      error: root.querySelector('[data-error]'),
      frequency: root.querySelector('[data-frequency]'),
      dock: root.querySelector('[data-dock]'),
      dockCount: root.querySelector('[data-dock-count]'),
      dockCheckout: root.querySelector('[data-dock-checkout]'),
      dockCheckoutLabel: root.querySelector('[data-dock-checkout-label]'),
      dockRequirement: root.querySelector('[data-dock-requirement]'),
      summaryNext: root.querySelector('[data-summary-next]'),
      statusChip: root.querySelector('[data-status-chip]'),
      dockTotal: root.querySelector('[data-dock-total]'),
      dockPill: root.querySelector('[data-dock-pill]'),
      dockProgress: root.querySelector('[data-dock-progress]'),
      backdrop: root.querySelector('[data-backdrop]'),
      sheetClose: root.querySelector('[data-sheet-close]'),
      modal: root.querySelector('[data-modal]'),
      modalImage: root.querySelector('[data-modal-image]'),
      modalTitle: root.querySelector('[data-modal-title]'),
      modalRating: root.querySelector('[data-modal-rating]'),
      modalDesc: root.querySelector('[data-modal-desc]'),
      modalPrice: root.querySelector('[data-modal-price]'),
      modalReviews: Array.prototype.slice.call(root.querySelectorAll('[data-modal-review]')),
      modalIngredients: root.querySelector('[data-modal-ingredients]'),
      modalNote: root.querySelector('[data-modal-note]'),
      modalBody: root.querySelector('.bb-modal__body'),
      modalScrollHint: root.querySelector('[data-modal-scrollhint]'),
      modalIngredientsBody: root.querySelector('[data-modal-ingredients-body]'),
      wall: root.querySelector('[data-wall]'),
      wallGrid: root.querySelector('[data-wall-grid]'),
      modalOptions: root.querySelector('[data-modal-options]'),
      modalAdd: root.querySelector('[data-modal-add]'),
      ctaNote: root.querySelector('[data-cta-note]'),
      busy: root.querySelector('[data-busy]'),
      busyText: root.querySelector('[data-busy-text]'),
      giftVariant: root.querySelector('[data-gift-variant]')
    };
    this.modalCard = null;

    if (this.el.grid && this.el.cards.length) {
      var grid = this.el.grid;
      this.el.cards = this.el.cards.slice().sort(function (a, b) {
        return (parseInt(a.getAttribute('data-order'), 10) || 999) - (parseInt(b.getAttribute('data-order'), 10) || 999);
      });
      this.el.cards.forEach(function (card) { grid.appendChild(card); });
      var giftCard = grid.querySelector('[data-gift-card]');
      if (giftCard) grid.insertBefore(giftCard, grid.firstChild);
    }
    this.bind();
    this.preselect();
    this.restore();
    this.addFromUrl();
    this.pinCards();
    Array.prototype.forEach.call(root.querySelectorAll('.bb-option select'), enhanceSelect);
    this.renderTabCounts();
    this.renderAllCards();
    this.renderSummary();
    this.renderWall();
    this._milestones = {};
    track('bb_view', { products: this.cfg.products.length, min_amount: (this.cfg.minAmount || 0) / 100 });
    this.prefetchDescriptions();
    this.prefetchPopupImages();
  }

  // Warm the description cache in the background (after load, on idle, 3 requests at a time)
  // so the first popup opens instantly.
  // Warm the browser cache with the popup-size photos, one at a time, only when the page is idle and
  // the connection is not metered. Popups then open with the image already there.
  BundleBuilder.prototype.prefetchPopupImages = function () {
    var conn = navigator.connection || {};
    if (conn.saveData || /(^|[^\d])2g/.test(conn.effectiveType || '')) return;
    var urls = [];
    var seen = {};
    this.cfg.products.forEach(function (p) {
      [p.imageLarge].concat((p.variants || []).map(function (v) { return v.image; })).forEach(function (u) {
        if (u && !seen[u]) { seen[u] = true; urls.push(u); }
      });
    });
    var loadNext = function () {
      if (!urls.length) return;
      var img = new Image();
      img.onload = img.onerror = function () {
        if (window.requestIdleCallback) window.requestIdleCallback(loadNext, { timeout: 2000 });
        else setTimeout(loadNext, 150);
      };
      img.src = urls.shift();
    };
    var begin = function () {
      if (window.requestIdleCallback) window.requestIdleCallback(loadNext, { timeout: 5000 });
      else setTimeout(loadNext, 2500);
    };
    if (document.readyState === 'complete') begin();
    else window.addEventListener('load', begin, { once: true });
  };

  BundleBuilder.prototype.prefetchDescriptions = function () {
    var self = this;
    var queue = this.cfg.products.filter(function (p) {
      return p.description === undefined;
    });
    var run = function () {
      var active = 0;
      var next = function () {
        while (active < 3 && queue.length) {
          var p = queue.shift();
          active += 1;
          self.fetchDescription(p).then(function () {
            active -= 1;
            next();
          });
        }
      };
      next();
    };
    var start = function () {
      if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 3000 });
      else setTimeout(run, 800);
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  };

  BundleBuilder.prototype.fetchDescription = function (p) {
    var self = this;
    if (p.description !== undefined) return Promise.resolve(p.description);
    if (p._descPromise) return p._descPromise;
    p._descPromise = fetch('/products/' + encodeURIComponent(p.handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        p.description = self.cleanHtml((data && data.description) || '');
        return p.description;
      })
      .catch(function () {
        p.description = '';
        return '';
      });
    return p._descPromise;
  };

  /* ---------- persistence ---------- */

  // The box is kept in localStorage (per page) for 7 days after it was last changed, so a reload, a
  // return from checkout or a visit a few days later shows the same selection. The preselected-box
  // page stores the template's preset alongside, so a saved box only replaces the preset it was built
  // from; an empty saved box keeps the preset.
  var SAVE_DAYS = 7;

  BundleBuilder.prototype.storageKey = function () {
    return 'bb-box:' + window.location.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i, '');
  };

  BundleBuilder.prototype.save = function () {
    if (!this._restored) return;
    var state = JSON.stringify({
      preset: this.cfg.preselect || [],
      pinned: this.pinned || [],
      frequency: this.el.frequency ? this.el.frequency.value : null,
      items: this.items.map(function (it) { return [it.productId, it.variantId]; })
    });
    // Only a real change restarts the 7 days; simply viewing the page does not.
    if (state === this._savedState) return;
    this._savedState = state;
    try {
      window.localStorage.setItem(this.storageKey(), JSON.stringify({ savedAt: Date.now(), state: state }));
    } catch (e) { /* storage full or blocked */ }
  };

  BundleBuilder.prototype.restore = function () {
    var self = this;
    this._restored = true;
    var saved = null;
    try {
      var entry = JSON.parse(window.localStorage.getItem(this.storageKey()) || 'null');
      if (!entry || !entry.state) return;
      if (!(Date.now() - entry.savedAt < SAVE_DAYS * 864e5)) {
        window.localStorage.removeItem(this.storageKey());
        return;
      }
      saved = JSON.parse(entry.state);
      this._savedState = entry.state;
    } catch (e) { return; }
    if (!saved || JSON.stringify(saved.preset || []) !== JSON.stringify(this.cfg.preselect || [])) return;
    this.pinned = saved.pinned || [];

    if (this.el.frequency && saved.frequency) {
      var hasOption = Array.prototype.some.call(this.el.frequency.options, function (o) { return o.value === saved.frequency; });
      if (hasOption) this.el.frequency.value = saved.frequency;
    }

    // Skip anything that has since been removed from the page or sold out.
    var items = [];
    (saved.items || []).forEach(function (pair) {
      var p = self.products[pair[0]];
      var v = self.variantById(pair[0], pair[1]);
      var card = self.cardFor(pair[0]);
      if (!p || !v || !v.available || !card) return;
      self.applyChoiceToCard(card, p.id + ':' + v.id);
      items.push({ productId: p.id, variantId: v.id, collectionId: p.collectionId });
    });
    if (!items.length) return;
    this.items = items;
    this._restoredBox = true;
  };

  // Push a choice into a card's dropdown(s) so the UI matches the state.
  BundleBuilder.prototype.applyChoiceToCard = function (card, choiceStr) {
    if (!card || !choiceStr) return;
    this.selectedChoice[card.getAttribute('data-card-key')] = choiceStr;
    var select = card.querySelector('[data-choice-select]');
    if (select) select.value = choiceStr;
    var optionSelects = card.querySelectorAll('[data-option-select]');
    if (optionSelects.length) {
      var c = this.parseChoice(choiceStr);
      var v = c ? this.variantById(c.productId, c.variantId) : null;
      if (v && v.options) {
        Array.prototype.forEach.call(optionSelects, function (sel, idx) {
          if (v.options[idx] !== undefined) sel.value = v.options[idx];
        });
      }
    }
  };

  // Template-level preselection ("handle" or "handle=Variant title" per entry).
  BundleBuilder.prototype.preselect = function () {
    this.addEntries(this.cfg.preselect || []);
  };

  // ?add=handle,handle,... from the URL (repeat a handle to add it more than once). Runs after the
  // saved box is restored so the products land on top of it, then drops the parameter so a reload
  // doesn't add them a second time.
  BundleBuilder.prototype.addFromUrl = function () {
    var params = new URLSearchParams(window.location.search);
    var fromUrl = params.get('add');
    if (!fromUrl) return;
    var added = this.addEntries(fromUrl.split(','));
    // Their cards move to the front of the grid (right after the free gift) for the rest of the visit.
    var pinned = [];
    added.forEach(function (card) {
      var key = card.getAttribute('data-card-key');
      if (pinned.indexOf(key) === -1) pinned.push(key);
    });
    if (pinned.length) this.pinned = pinned;
    params.delete('add');
    var query = params.toString();
    try {
      window.history.replaceState(window.history.state, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash);
    } catch (e) { /* ignore */ }
  };

  // Adds each entry to the box; returns the cards of the products that were added.
  BundleBuilder.prototype.addEntries = function (entries) {
    var self = this;
    var cards = [];
    entries.forEach(function (entry) {
      var parts = String(entry).split('=');
      var handle = parts[0].trim();
      var variantTitle = (parts[1] || '').trim().toLowerCase();
      var p = null;
      Object.keys(self.products).forEach(function (id) {
        if (!p && self.products[id].handle === handle) p = self.products[id];
      });
      if (!p) return;
      var v = null;
      p.variants.forEach(function (candidate) {
        if (v) return;
        if (variantTitle ? candidate.title.toLowerCase() === variantTitle : candidate.available) v = candidate;
      });
      if (!v || !v.available) return;
      var card = self.cardFor(p.id);
      if (!card) return;
      self.applyChoiceToCard(card, p.id + ':' + v.id);
      self.items.push({ productId: p.id, variantId: v.id, collectionId: p.collectionId });
      cards.push(card);
    });
    return cards;
  };

  // On load, cards of products already in the box move to the front (right after the free gift):
  // ?add= products first, then the rest of a restored box in the order it was built. A first visit
  // keeps the template order (so the preselected box page looks as designed), and nothing moves while
  // the customer is adding products.
  BundleBuilder.prototype.pinCards = function () {
    if (!this.el.grid) return;
    var self = this;
    var grid = this.el.grid;
    var byKey = {};
    this.el.cards.forEach(function (card) { byKey[card.getAttribute('data-card-key')] = card; });
    // Only cards that still have something in the box: an emptied box falls back to the template order.
    var front = (this.pinned || []).map(function (key) { return byKey[key]; }).filter(function (card) {
      return card && self.cardQty(card) > 0;
    });
    if (this._restoredBox) {
      this.items.forEach(function (it) {
        var card = self.cardFor(it.productId);
        if (card && front.indexOf(card) === -1) front.push(card);
      });
    }
    if (!front.length) return;
    this.el.cards = front.concat(this.el.cards.filter(function (card) { return front.indexOf(card) === -1; }));
    var prev = grid.querySelector('[data-gift-card]');
    front.forEach(function (card) {
      grid.insertBefore(card, prev ? prev.nextSibling : grid.firstChild);
      prev = card;
    });
  };

  BundleBuilder.prototype.renderTabCounts = function () {
    var self = this;
    this.el.tabs.forEach(function (tab) {
      var id = tab.getAttribute('data-tab');
      var n = self.el.cards.filter(function (card) {
        return id === 'all' || card.getAttribute('data-tab') === id;
      }).length;
      var el = tab.querySelector('[data-tab-count]');
      if (el) el.textContent = ' · ' + n;
    });
  };

  BundleBuilder.prototype.bind = function () {
    var self = this;

    this.el.tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        self.setTab(tab.getAttribute('data-tab'));
      });
    });

    this.el.cards.forEach(function (card) {
      var select = card.querySelector('[data-choice-select]');
      if (select) {
        select.addEventListener('change', function () {
          self.selectedChoice[card.getAttribute('data-card-key')] = select.value;
          self.renderCard(card);
        });
      }
      Array.prototype.forEach.call(card.querySelectorAll('[data-option-select]'), function (sel) {
        sel.addEventListener('change', function () {
          self.selectedChoice[card.getAttribute('data-card-key')] = self.resolveOptionChoice(card);
          self.renderCard(card);
        });
      });
      card.addEventListener('click', function (evt) {
        if (evt.target.closest('[data-open-details]')) {
          self.openDetails(card);
          return;
        }
        var btn = evt.target.closest('[data-add], [data-inc], [data-dec]');
        if (!btn || btn.disabled) return;
        var choice = self.currentChoice(card);
        if (!choice) return;
        if (btn.hasAttribute('data-dec')) {
          self.removeOne(choice.productId, choice.variantId);
        } else {
          self.addItem(card);
        }
      });
    });

    var giftCard = this.root.querySelector('[data-gift-card]');
    if (giftCard) {
      giftCard.addEventListener('click', function (evt) {
        if (evt.target.closest('[data-gift-details]')) self.openGiftDetails(giftCard);
      });
    }

    if (this.el.slots) {
      this.el.slots.addEventListener('click', function (evt) {
        var remove = evt.target.closest('[data-remove-index]');
        if (remove) {
          self.removeAt(Number(remove.getAttribute('data-remove-index')));
          return;
        }
        if (evt.target.closest('[data-empty-slot]')) {
          self.closeSheet();
          self.setTab('all');
          self.scrollToGrid();
          return;
        }
        // Tap on a filled tile shows its name (touch devices have no hover).
        var filled = evt.target.closest('.bb-slot.is-filled');
        if (filled) {
          var wasOpen = filled.classList.contains('is-tip');
          self.clearSlotTips();
          if (!wasOpen) {
            filled.classList.add('is-tip');
            clearTimeout(self._tipTimer);
            self._tipTimer = setTimeout(function () { self.clearSlotTips(); }, 2500);
          }
        }
      });
      document.addEventListener('click', function (evt) {
        if (!evt.target.closest('.bb-slot.is-filled')) self.clearSlotTips();
      });
    }

    if (this.el.frequency) {
      this.el.frequency.addEventListener('change', function () {
        self.renderAllCards();
        self.renderSummary();
      });
    }

    if (this.el.cta) {
      this.el.cta.addEventListener('click', function () {
        self.submit();
      });
    }

    if (this.el.dockCheckout) {
      this.el.dockCheckout.addEventListener('click', function () { self.submit(); });
    }

    if (this.el.dock) {
      this.el.dock.addEventListener('click', function () {
        self.toggleSheet();
      });
    }
    if (this.el.backdrop) {
      this.el.backdrop.addEventListener('click', function () {
        self.closeSheet();
      });
    }
    if (this.el.sheetClose) {
      this.el.sheetClose.addEventListener('click', function () {
        self.closeSheet();
      });
    }

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-faq-toggle]'), function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('.bb-faq__item');
        var open = !item.classList.contains('is-open');
        item.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });

    if (this.el.modal) {
      // "Scroll for more" pill: shown while content remains below the sticky footer.
      if (this.el.modalBody && this.el.modalScrollHint) {
        var body = this.el.modalBody;
        var hint = this.el.modalScrollHint;
        var footer = body.querySelector('.bb-modal__footer');
        this.updateScrollHint = function () {
          var atEnd = body.scrollHeight - body.scrollTop - body.clientHeight < 40;
          hint.hidden = atEnd;
          if (footer) footer.classList.toggle('is-at-end', atEnd);
        };
        body.addEventListener('scroll', this.updateScrollHint, { passive: true });
        hint.addEventListener('click', function () {
          body.scrollBy({ top: Math.round(body.clientHeight * 0.7), behavior: 'smooth' });
        });
      }
      Array.prototype.forEach.call(this.el.modal.querySelectorAll('[data-modal-close]'), function (el) {
        el.addEventListener('click', function () {
          self.closeDetails();
        });
      });
      document.addEventListener('keydown', function (evt) {
        if (evt.key === 'Escape' && !self.el.modal.hidden) self.closeDetails();
      });
      if (this.el.modalAdd) {
        this.el.modalAdd.addEventListener('click', function () {
          if (!self._giftModal && self.modalCard) self.addItem(self.modalCard);
          self.closeDetails();
        });
      }
    }
  };

  /* ---------- details modal ---------- */

  BundleBuilder.prototype.openDetails = function (card) {
    if (!this.el.modal) return;
    var self = this;
    this.modalCard = card;
    track('bb_details', { product: card.getAttribute('data-card-title') });

    // Mirror the card's dropdowns inside the popup; changes flow back to the card.
    if (this.el.modalOptions) {
      this.el.modalOptions.innerHTML = '';
      Array.prototype.forEach.call(card.querySelectorAll('.bb-option'), function (optionEl) {
        var clone = optionEl.cloneNode(true);
        var original = optionEl.querySelector('select');
        var mirror = clone.querySelector('select');
        Array.prototype.forEach.call(clone.querySelectorAll('.bb-select'), function (ui) {
          ui.parentNode.removeChild(ui);
        });
        mirror.classList.remove('bb-visually-hidden');
        mirror.removeAttribute('aria-hidden');
        mirror.tabIndex = 0;
        mirror.value = original.value;
        mirror.addEventListener('change', function () {
          original.value = mirror.value;
          original.dispatchEvent(new Event('change', { bubbles: true }));
          self.updateDetails(card);
        });
        self.el.modalOptions.appendChild(clone);
        enhanceSelect(mirror);
      });
    }

    if (!this.updateDetails(card)) return;
    this.el.modal.hidden = false;
    if (this.el.modalBody) this.el.modalBody.scrollTop = 0;
    this.refreshScrollHint();
    document.documentElement.classList.add('bb-modal-lock');
  };

  // Details popup for the free gift: same modal, no options, no add button, description fetched by handle.
  BundleBuilder.prototype.openGiftDetails = function (card) {
    if (!this.el.modal) return;
    this.modalCard = card;
    if (this.el.modalOptions) this.el.modalOptions.innerHTML = '';
    this.el.modalTitle.textContent = card.getAttribute('data-gift-title') || '';
    this.el.modalImage.src = card.getAttribute('data-gift-image') || '';
    this.el.modalImage.alt = this.el.modalTitle.textContent;
    var label = card.querySelector('.bb-card__gift-label');
    this.el.modalRating.innerHTML = label ? '<span class="bb-card__gift-label">' + escapeHtml(label.textContent) + '</span>' : '';
    var price = card.querySelector('.bb-card__price');
    if (this.el.modalPrice) this.el.modalPrice.innerHTML = price ? price.innerHTML : '';
    if (!this._gift) this._gift = { handle: card.getAttribute('data-gift-handle') };
    this.loadDescription(this._gift);
    this._giftModal = true;
    if (this.el.modalNote) this.el.modalNote.hidden = true;
    this.renderModalReview(card.getAttribute('data-gift-title'));
    this.setIngredients(card.getAttribute('data-gift-ingredients'));
    this.el.modalAdd.hidden = false;
    this.el.modalAdd.disabled = false;
    this.el.modalAdd.textContent = this.cfg.text.giftClose || 'Go back';
    this.el.modal.hidden = false;
    if (this.el.modalBody) this.el.modalBody.scrollTop = 0;
    this.refreshScrollHint();
    document.documentElement.classList.add('bb-modal-lock');
  };

  // Hand-picked review for the card (keyed by card title in snippets/bundle-builder-reviews.liquid).
  BundleBuilder.prototype.renderModalReview = function (key) {
    var rv = key && this.cfg.reviews ? this.cfg.reviews[key] : null;
    var html = rv
      ? '<span class="bb-stars" style="--bb-rating:100%" aria-hidden="true"></span>' +
        '<p class="bb-modal__review-title">' + escapeHtml(rv.title) + '</p>' +
        '<p class="bb-modal__review-body">' + escapeHtml(rv.body) + '</p>' +
        '<p class="bb-modal__review-meta">' + escapeHtml(rv.name) + '</p>'
      : '';
    this.el.modalReviews.forEach(function (el) {
      el.innerHTML = html;
      el.hidden = !rv;
    });
  };

  // Review wall at the bottom of the page: every hand-picked review, tagged with the product it is about.
  BundleBuilder.prototype.renderWall = function () {
    var self = this;
    var t = this.cfg.text;
    if (!this.el.wall || !this.el.wallGrid || !this.cfg.reviews) return;
    var html = '';
    var seen = {};
    this.el.cards.forEach(function (card) {
      var key = card.getAttribute('data-card-title');
      var rv = key && self.cfg.reviews[key];
      if (!rv || seen[key]) return;
      seen[key] = true;
      var img = card.querySelector('[data-card-image]');
      var src = img ? img.currentSrc || img.src : '';
      html +=
        '<article class="bb-wall__card">' +
        '<span class="bb-stars" style="--bb-rating:100%" aria-hidden="true"></span>' +
        '<h3 class="bb-wall__rtitle">' + escapeHtml(rv.title) + '</h3>' +
        '<p class="bb-wall__body">' + escapeHtml(rv.body) + '</p>' +
        '<p class="bb-wall__meta">' + escapeHtml(rv.name) + '</p>' +
        '<button type="button" class="bb-wall__product" data-wall-open="' + escapeHtml(key) + '">' +
        (src ? '<img src="' + escapeHtml(src) + '" alt="" loading="lazy">' : '') +
        '<span><span class="bb-wall__product-label">' + escapeHtml(t.wallReviewing) + '</span>' + escapeHtml(key) + '</span><svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
        '</article>';
    });
    var giftCard = this.root.querySelector('[data-gift-card]');
    var giftKey = giftCard && giftCard.getAttribute('data-gift-title');
    var giftRv = giftKey && this.cfg.reviews[giftKey];
    if (giftRv) {
      var gimg = giftCard.querySelector('.bb-card__media img');
      html +=
        '<article class="bb-wall__card">' +
        '<span class="bb-stars" style="--bb-rating:100%" aria-hidden="true"></span>' +
        '<h3 class="bb-wall__rtitle">' + escapeHtml(giftRv.title) + '</h3>' +
        '<p class="bb-wall__body">' + escapeHtml(giftRv.body) + '</p>' +
        '<p class="bb-wall__meta">' + escapeHtml(giftRv.name) + '</p>' +
        '<button type="button" class="bb-wall__product" data-wall-gift>' +
        (gimg ? '<img src="' + escapeHtml(gimg.currentSrc || gimg.src) + '" alt="" loading="lazy">' : '') +
        '<span><span class="bb-wall__product-label">' + escapeHtml(t.wallReviewing) + '</span>' + escapeHtml(giftKey) + '</span><svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
        '</article>';
    }
    this.el.wallGrid.innerHTML = html;
    if (!html) { this.el.wall.hidden = true; return; }
    this.el.wallGrid.addEventListener('click', function (evt) {
      if (evt.target.closest('[data-wall-gift]') && giftCard) { self.openGiftDetails(giftCard); return; }
      var btn = evt.target.closest('[data-wall-open]');
      if (!btn) return;
      var card = self.el.cards.filter(function (c) { return c.getAttribute('data-card-title') === btn.getAttribute('data-wall-open'); })[0];
      if (card) self.openDetails(card);
    });
  };

  BundleBuilder.prototype.setIngredients = function (html) {
    if (!this.el.modalIngredients) return;
    var clean = html ? this.cleanHtml(html).trim() : '';
    if (clean) {
      // Some metafields end with a PDP-only "scroll down" prompt; drop that element (or the bare text).
      var box = document.createElement('div');
      box.innerHTML = clean;
      Array.prototype.forEach.call(box.querySelectorAll('*'), function (el) {
        if (el.children.length === 0 && /(scroll\s*down\s*for\s*more|d[ée]filez\s*vers\s*le\s*bas)/i.test(el.textContent)) el.parentNode.removeChild(el);
      });
      clean = box.innerHTML.replace(/(scroll\s*down\s*for\s*more\s*details|d[ée]filez\s*vers\s*le\s*bas\s*pour\s*plus\s*de\s*d[ée]tails)\s*[▼▾↓]?/gi, '').trim();
    }
    this.el.modalIngredients.hidden = !clean;
    this.el.modalIngredients.open = false;
    if (this.el.modalIngredientsBody) this.el.modalIngredientsBody.innerHTML = clean;
  };

  BundleBuilder.prototype.updateDetails = function (card) {
    this._giftModal = false;
    this.renderModalReview(card.getAttribute('data-card-title'));
    var choice = this.currentChoice(card);
    var p = choice ? this.products[choice.productId] : null;
    if (!p) return false;
    var v = this.variantById(choice.productId, choice.variantId);
    this.el.modalTitle.textContent = card.getAttribute('data-card-title') || p.title;
    this.el.modalImage.src = (v && v.image) || p.imageLarge || p.image || '';
    this.el.modalImage.alt = p.title;
    this.el.modalRating.innerHTML = this.ratingHtml(p);
    this.setIngredients(p.ingredients);
    if (this.el.modalNote) { this.el.modalNote.textContent = p.note || ''; this.el.modalNote.hidden = !p.note; }
    if (this.el.modalPrice) {
      var mp = v ? this.priceHtml(this.unitPrice(v)) : '';
      if (this.el.modalPrice.innerHTML !== mp) this.el.modalPrice.innerHTML = mp;
    }
    this.loadDescription(p);
    var canAdd = !!(v && v.available);
    this.el.modalAdd.hidden = !v;
    this.el.modalAdd.disabled = !canAdd;
    this.el.modalAdd.textContent = v && !v.available ? this.cfg.text.soldOut : this.cfg.text.add;
    return true;
  };

  // Descriptions are fetched on demand (keeps ~30 KB of HTML out of the initial page load).
  BundleBuilder.prototype.loadDescription = function (p) {
    var self = this;
    var desc = this.el.modalDesc;
    if (p.description !== undefined) {
      desc.innerHTML = p.description;
      return;
    }
    desc.innerHTML = '';
    var token = (this._descToken = (this._descToken || 0) + 1);
    this.fetchDescription(p).then(function (html) {
      if (token === self._descToken && self.modalCard) desc.innerHTML = html;
    });
  };

  // Strip inline styling and stray tags from product descriptions so they inherit page typography.
  BundleBuilder.prototype.cleanHtml = function (html) {
    var box = document.createElement('div');
    box.innerHTML = html;
    Array.prototype.forEach.call(box.querySelectorAll('meta, script, style, iframe, link'), function (el) {
      el.parentNode.removeChild(el);
    });
    Array.prototype.forEach.call(box.querySelectorAll('*'), function (el) {
      el.removeAttribute('style');
      el.removeAttribute('data-mce-style');
      el.removeAttribute('data-mce-fragment');
      el.removeAttribute('color');
      el.removeAttribute('face');
      el.removeAttribute('size');
      el.removeAttribute('width');
      el.removeAttribute('height');
    });
    return box.innerHTML;
  };

  BundleBuilder.prototype.refreshScrollHint = function () {
    var self = this;
    if (!this.updateScrollHint) return;
    this.updateScrollHint();
    // Description and image load after open; re-check a few times.
    [150, 500, 1200].forEach(function (ms) { setTimeout(self.updateScrollHint, ms); });
  };

  BundleBuilder.prototype.closeDetails = function () {
    if (!this.el.modal) return;
    closeAllSelects();
    this.el.modal.hidden = true;
    this.modalCard = null;
    document.documentElement.classList.remove('bb-modal-lock');
  };

  // Price with the active tier discount applied, or a hint about the first tier.
  BundleBuilder.prototype.priceHtml = function (price) {
    var t = this.cfg.text;
    var fmt = this.cfg.moneyFormat;
    var totals = this.totals();
    if (totals.pct > 0) {
      var discounted = price - Math.round((price * totals.pct) / 100);
      return (
        '<s class="bb-price__was">' + formatMoney(price, fmt) + '</s> ' +
        '<span class="bb-price__now">' + formatMoney(discounted, fmt) + '</span>'
      );
    }
    return '<span class="bb-price__now">' + formatMoney(price, fmt) + '</span>';
  };

  BundleBuilder.prototype.ratingHtml = function (p) {
    var rating = Number(p.rating) || 0;
    var count = Number(p.ratingCount) || 0;
    return (
      '<span class="bb-stars" style="--bb-rating:' + Math.round(rating * 20) + '%" aria-hidden="true"></span>' +
      '<span class="bb-card__rating-text">' + (rating ? rating.toFixed(1) + ' (' + count + ')' : '(' + count + ')') + '</span>' +
      (p.isNew ? '<span class="bb-new">New</span>' : '')
    );
  };

  // Multi-option products (e.g. Scent + SPF): find the variant matching every selected option.
  BundleBuilder.prototype.resolveOptionChoice = function (card) {
    var productId = Number(card.getAttribute('data-product'));
    var p = this.products[productId];
    if (!p) return null;
    var values = Array.prototype.map.call(card.querySelectorAll('[data-option-select]'), function (sel) {
      return sel.value;
    });
    for (var i = 0; i < p.variants.length; i++) {
      var opts = p.variants[i].options || [];
      var match = values.every(function (val, idx) {
        return opts[idx] === val;
      });
      if (match) return productId + ':' + p.variants[i].id;
    }
    return productId + ':0';
  };

  /* ---------- state helpers ---------- */

  BundleBuilder.prototype.currentPlan = function () {
    var plans = this.cfg.parent.plans || [];
    if (this.el.frequency) {
      var id = Number(this.el.frequency.value);
      for (var i = 0; i < plans.length; i++) if (plans[i].id === id) return plans[i];
    }
    for (var j = 0; j < plans.length; j++) {
      if (plans[j].freq === this.cfg.defaultFrequency) return plans[j];
    }
    return plans[0] || null;
  };

  BundleBuilder.prototype.childPlan = function (variant) {
    var parentPlan = this.currentPlan();
    if (!parentPlan || !variant.plans) return null;
    var byFreq = variant.plans.filter(function (p) {
      return p.freq && p.freq === parentPlan.freq;
    });
    if (byFreq.length) return byFreq[0];
    var byName = variant.plans.filter(function (p) {
      return p.name && parentPlan.name && p.name.toLowerCase() === parentPlan.name.toLowerCase();
    });
    return byName[0] || null;
  };

  BundleBuilder.prototype.variantById = function (productId, variantId) {
    var p = this.products[productId];
    if (!p) return null;
    for (var i = 0; i < p.variants.length; i++) if (p.variants[i].id === variantId) return p.variants[i];
    return null;
  };

  BundleBuilder.prototype.parseChoice = function (str) {
    if (!str) return null;
    var parts = String(str).split(':');
    return { productId: Number(parts[0]), variantId: Number(parts[1]) };
  };

  // The product/variant currently selected on a card (dropdown value or the single choice).
  BundleBuilder.prototype.currentChoice = function (card) {
    if (!card) return null;
    var key = card.getAttribute('data-card-key');
    var select = card.querySelector('[data-choice-select]');
    var value = this.selectedChoice[key];
    if (!value && card.querySelector('[data-option-select]')) value = this.resolveOptionChoice(card);
    if (!value) value = select ? select.value : card.getAttribute('data-choice');
    return this.parseChoice(value);
  };

  BundleBuilder.prototype.unitPrice = function (variant) {
    var plan = this.childPlan(variant);
    return plan && typeof plan.price === 'number' ? plan.price : variant.price;
  };

  BundleBuilder.prototype.qtyFor = function (productId, variantId) {
    return this.items.filter(function (it) {
      return it.productId === productId && it.variantId === variantId;
    }).length;
  };

  // Everything in the box that belongs to this card, across all its scents/options.
  BundleBuilder.prototype.cardQty = function (card) {
    var self = this;
    return this.items.filter(function (it) {
      return self.cardFor(it.productId) === card;
    }).length;
  };

  // Tag each scent/option in the card's dropdown with how many of it are in the box.
  BundleBuilder.prototype.renderOptionCounts = function (card) {
    var self = this;
    var choiceSelect = card.querySelector('[data-choice-select]');
    var optionSelects = card.querySelectorAll('[data-option-select]');
    var tag = function (select, countFor) {
      var changed = false;
      Array.prototype.forEach.call(select.options, function (opt) {
        var n = String(countFor(opt.value) || '');
        if ((opt.getAttribute('data-in-box') || '') !== n) {
          if (n) opt.setAttribute('data-in-box', n);
          else opt.removeAttribute('data-in-box');
          changed = true;
        }
      });
      if (changed && select.__bbRefresh) select.__bbRefresh();
    };
    if (choiceSelect) {
      tag(choiceSelect, function (value) {
        var c = self.parseChoice(value);
        return c ? self.qtyFor(c.productId, c.variantId) : 0;
      });
    } else if (optionSelects.length === 1) {
      // Single-option products only: with several options a count per value would be ambiguous.
      var productId = Number(card.getAttribute('data-product'));
      tag(optionSelects[0], function (value) {
        return self.items.filter(function (it) {
          var v = it.productId === productId ? self.variantById(it.productId, it.variantId) : null;
          return v && v.options && v.options[0] === value;
        }).length;
      });
    }
  };

  BundleBuilder.prototype.totals = function () {
    var count = this.items.length;
    var subtotal = 0;
    var self = this;
    var unitPrices = this.items.map(function (it) {
      var v = self.variantById(it.productId, it.variantId);
      var price = v ? self.unitPrice(v) : 0;
      subtotal += price;
      return price;
    });
    var current = null;
    var next = null;
    this.cfg.tiers.forEach(function (t) {
      if (count >= t.qty) current = t;
      else if (!next) next = t;
    });
    var pct = current ? current.pct : 0;
    var discount = 0;
    unitPrices.forEach(function (price) {
      discount += Math.round((price * pct) / 100);
    });
    return {
      count: count,
      subtotal: subtotal,
      discount: discount,
      total: subtotal - discount,
      pct: pct,
      current: current,
      next: next
    };
  };

  /* ---------- actions ---------- */

  BundleBuilder.prototype.addItem = function (card) {
    var choice = this.currentChoice(card);
    if (!choice) return;
    var p = this.products[choice.productId];
    var v = this.variantById(choice.productId, choice.variantId);
    if (!p || !v || !v.available) return;
    this.items.push({ productId: choice.productId, variantId: choice.variantId, collectionId: p.collectionId });
    this.hideError();
    this.renderCard(card);
    this.renderSummary();
    var tt = this.totals();
    track('bb_add', { product: p.title, variant: v.title, price: this.unitPrice(v) / 100, items: tt.count, total: tt.total / 100, tier_pct: tt.pct });
  };

  BundleBuilder.prototype.removeOne = function (productId, variantId) {
    for (var i = this.items.length - 1; i >= 0; i--) {
      if (this.items[i].productId === productId && this.items[i].variantId === variantId) {
        this.removeAt(i);
        return;
      }
    }
  };

  // If the card was showing the option that just left the box and other options of the card are still
  // in it, switch the card to the most recently added one so its +/- controls stay in reach.
  BundleBuilder.prototype.showRemainingChoice = function (card, removed) {
    if (!card) return;
    var shown = this.currentChoice(card);
    if (!shown || shown.productId !== removed.productId || shown.variantId !== removed.variantId) return;
    if (this.qtyFor(shown.productId, shown.variantId) > 0) return;
    var self = this;
    var next = null;
    for (var i = this.items.length - 1; i >= 0 && !next; i--) {
      if (this.cardFor(this.items[i].productId) === card) next = this.items[i];
    }
    if (!next) return;
    this.applyChoiceToCard(card, next.productId + ':' + next.variantId);
    // Let the custom dropdowns redraw their button.
    Array.prototype.forEach.call(card.querySelectorAll('[data-choice-select], [data-option-select]'), function (sel) {
      if (sel.__bbRefresh) sel.__bbRefresh();
    });
  };

  // Fade the tile out, then drop the item and re-render.
  BundleBuilder.prototype.removeAt = function (index) {
    var removed = this.items[index];
    if (removed) {
      var rp = this.products[removed.productId];
      track('bb_remove', { product: rp ? rp.title : '', items: this.items.length - 1 });
    }
    var self = this;
    var it = this.items[index];
    if (!it || this._removing) return;
    var tile = this.el.slots ? this.el.slots.children[index] : null;
    var finish = function () {
      self._removing = false;
      var idx = self.items.indexOf(it);
      if (idx > -1) self.items.splice(idx, 1);
      var card = self.cardFor(it.productId);
      self.showRemainingChoice(card, it);
      self.renderCard(card);
      self.renderSummary();
    };
    if (tile && tile.classList.contains('is-filled') && !prefersReducedMotion()) {
      this._removing = true;
      tile.classList.add('is-leaving');
      setTimeout(finish, 160);
    } else {
      finish();
    }
  };

  BundleBuilder.prototype.setTab = function (tabId) {
    var grid = this.el.grid;
    if (grid) {
      grid.classList.remove('is-switching');
      void grid.offsetWidth; // restart the enter animation
      grid.classList.add('is-switching');
    }
    this.el.tabs.forEach(function (tab) {
      tab.classList.toggle('is-active', tab.getAttribute('data-tab') === tabId);
    });
    var activeTab = this.el.tabs.filter(function (tab) { return tab.getAttribute('data-tab') === tabId; })[0];
    track('bb_tab', { category: activeTab ? activeTab.textContent.replace(/\d+$/, '').trim() : tabId });
    this.el.cards.forEach(function (card) {
      card.classList.toggle('is-hidden', tabId !== 'all' && card.getAttribute('data-tab') !== tabId);
    });
    var giftCard = this.root.querySelector('[data-gift-card]');
    if (giftCard) giftCard.classList.toggle('is-hidden', tabId !== 'all' && tabId !== giftCard.getAttribute('data-gift-tab'));
  };

  BundleBuilder.prototype.clearSlotTips = function () {
    if (!this.el.slots) return;
    Array.prototype.forEach.call(this.el.slots.querySelectorAll('.bb-slot.is-tip'), function (s) {
      s.classList.remove('is-tip');
    });
  };

  BundleBuilder.prototype.scrollToGrid = function () {
    if (this.el.grid) this.el.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  BundleBuilder.prototype.toggleSheet = function () {
    this.root.classList.toggle('is-sheet-open');
    var open = this.root.classList.contains('is-sheet-open');
    if (this.el.dock) this.el.dock.setAttribute('aria-expanded', open ? 'true' : 'false');
    document.documentElement.classList.toggle('bb-sheet-lock', open);
  };

  BundleBuilder.prototype.closeSheet = function () {
    if (this.root.classList.contains('is-sheet-open')) this.toggleSheet();
  };

  /* ---------- rendering ---------- */

  BundleBuilder.prototype.cardFor = function (productId) {
    for (var i = 0; i < this.el.cards.length; i++) {
      var card = this.el.cards[i];
      var single = this.parseChoice(card.getAttribute('data-choice'));
      if (single && single.productId === productId) return card;
      var select = card.querySelector('[data-choice-select]');
      if (select) {
        for (var j = 0; j < select.options.length; j++) {
          var c = this.parseChoice(select.options[j].value);
          if (c && c.productId === productId) return card;
        }
      }
    }
    return null;
  };

  BundleBuilder.prototype.renderAllCards = function () {
    this.el.cards.forEach(this.renderCard, this);
  };

  BundleBuilder.prototype.renderCard = function (card) {
    if (!card) return;
    var t = this.cfg.text;
    var choice = this.currentChoice(card);
    var variant = choice ? this.variantById(choice.productId, choice.variantId) : null;
    var qty = variant ? this.qtyFor(choice.productId, choice.variantId) : 0;
    var inBox = qty > 0;
    var priceEl = card.querySelector('[data-price]');
    var actionEl = card.querySelector('[data-action]');
    var select = card.querySelector('[data-choice-select]');

    // Swap image and rating to match the selected option.
    var img = card.querySelector('[data-card-image]');
    var newSrc = null;
    if (select && select.selectedIndex > -1) {
      newSrc = select.options[select.selectedIndex].getAttribute('data-image');
    } else if (variant && variant.image) {
      newSrc = variant.image;
    }
    if (img && newSrc && img.getAttribute('src') !== newSrc) {
      img.removeAttribute('srcset');
      img.setAttribute('src', newSrc);
    }
    var ratingEl = card.querySelector('[data-card-rating]');
    var product = choice ? this.products[choice.productId] : null;
    if (ratingEl && product) ratingEl.innerHTML = this.ratingHtml(product);

    var cardQty = this.cardQty(card);
    this.renderOptionCounts(card);
    card.classList.toggle('in-box', cardQty > 0);
    var countEl = card.querySelector('[data-count]');
    if (countEl) {
      countEl.hidden = cardQty < 1;
      countEl.textContent = '\u00d7' + cardQty;
    }
    if (priceEl) {
      var priceHtml = variant ? this.priceHtml(this.unitPrice(variant)) : '';
      if (priceEl.innerHTML !== priceHtml) {
        var animate = priceEl.innerHTML !== '';
        priceEl.innerHTML = priceHtml;
        if (animate) restartAnimation(priceEl, 'is-ticking');
      }
    }

    if (!actionEl) return;
    var wasStepper = !!actionEl.querySelector('.bb-stepper');
    var willBeStepper = !!(variant && variant.available && inBox);
    if (actionEl.innerHTML !== '' && wasStepper !== willBeStepper) restartAnimation(actionEl, 'is-swapping');
    if (!variant || !variant.available) {
      actionEl.innerHTML = '<button type="button" class="bb-add" disabled>' + escapeHtml(t.soldOut) + '</button>';
    } else if (inBox) {
      actionEl.innerHTML =
        '<div class="bb-stepper">' +
        '<button type="button" data-dec aria-label="Remove one">&minus;</button>' +
        '<span>' + qty + '</span>' +
        '<button type="button" data-inc aria-label="Add one">+</button>' +
        '</div>';
    } else {
      actionEl.innerHTML = '<button type="button" class="bb-add" data-add>' + escapeHtml(t.add) + '</button>';
    }
  };

  BundleBuilder.prototype.renderProgress = function (totals) {
    var t = this.cfg.text;
    var segs = Math.max(this.cfg.segments || 0, this.cfg.minItems || 0, 1);
    var labels = '';
    var bar = '';
    var dock = '';

    this.cfg.tiers.forEach(function (tier) {
      if (tier.qty > segs) return;
      labels +=
        '<span class="bb-progress__label" style="grid-column:' + tier.qty + '">' +
        '<span class="bb-progress__full">' + escapeHtml(tpl(t.progressLabel, { qty: tier.qty })) +
        '<br><span class="bb-save">' + escapeHtml(tpl(t.progressSave, { pct: tier.pct })) + '</span></span>' +
        '<span class="bb-progress__pct">' + escapeHtml(tpl(t.progressPct, { pct: tier.pct })) + '</span></span>';
    });
    for (var i = 1; i <= segs; i++) {
      var filled = i <= totals.count ? ' is-filled' : '';
      bar += '<div class="bb-progress__seg' + filled + '"></div>';
      dock += '<span class="bb-dock__seg' + filled + '"></span>';
    }
    if (this.el.progressLabels) this.el.progressLabels.innerHTML = labels;
    if (this.el.progressBar) this.el.progressBar.innerHTML = bar;
    if (this.el.progressNext) {
      this.el.progressNext.textContent = totals.next
        ? tpl(t.tierNext, { count: totals.next.qty - totals.count, pct: totals.next.pct })
        : totals.current ? tpl(t.tierMax, { pct: totals.current.pct }) : '';
    }
    if (this.el.dockProgress) this.el.dockProgress.innerHTML = dock;
  };

  // 3-column slot grid: one tile per unit up to the top tier. Filled tiles show the photo with the
  // name and chosen option below it; tier tiles (3/5/7) are outlined and labelled.
  BundleBuilder.prototype.renderSlots = function (totals) {
    if (!this.el.slots) return;
    var t = this.cfg.text;
    var self = this;
    var segs = Math.max(this.cfg.segments || 0, this.cfg.minItems || 0, 1);
    var slotCount = Math.max(segs, this.items.length);
    var rewardAt = {};
    this.cfg.tiers.forEach(function (tier) {
      rewardAt[tier.qty] = tier.pct;
    });

    var html = '';
    for (var i = 1; i <= slotCount; i++) {
      var it = this.items[i - 1];
      if (it) {
        var p = self.products[it.productId];
        var v = self.variantById(it.productId, it.variantId);
        var card = self.cardFor(it.productId);
        var name = (card && card.getAttribute('data-card-title')) || (p ? p.title : '');
        var optionText = '';
        if (card) {
          var choiceSelect = card.querySelector('[data-choice-select]');
          if (choiceSelect) {
            var opt = Array.prototype.filter.call(choiceSelect.options, function (o) {
              return o.value === it.productId + ':' + it.variantId;
            })[0];
            if (opt) optionText = opt.textContent.trim();
          } else if (card.querySelector('[data-option-select]') && v) {
            optionText = v.title;
          }
        }
        var imgSrc = (v && v.image) || (p && p.image) || '';
        html +=
          '<div class="bb-slot is-filled" data-tip="' + escapeHtml(name + (optionText ? ' · ' + optionText : '')) + '">' +
          '<div class="bb-slot__media">' + (imgSrc ? '<img src="' + escapeHtml(imgSrc) + '" alt="' + escapeHtml(name + (optionText ? ', ' + optionText : '')) + '" loading="lazy">' : '') + '</div>' +
          '<button type="button" class="bb-slot__remove" data-remove-index="' + (i - 1) + '" aria-label="' + escapeHtml(t.remove + ' ' + name) + '">&times;</button>' +
          '</div>';
      } else {
        html +=
          '<div class="bb-slot' + (rewardAt[i] ? ' is-tier' : '') + '" data-empty-slot role="button" tabindex="0">' +
          '<span class="bb-slot__num">' + i + '</span>' +
          (rewardAt[i] ? '<span class="bb-slot__reward">' + escapeHtml(tpl(t.slotReward, { pct: rewardAt[i] })) + '</span>' : '') +
          (rewardAt[i] ? '' : '<span class="bb-slot__plus" aria-hidden="true"></span><span class="bb-visually-hidden">' + escapeHtml(t.slotEmpty) + '</span>') +
          '</div>';
      }
    }
    this.el.slots.innerHTML = html;
    // Scale in tiles that were just added.
    var prevCount = this._prevItemCount || 0;
    if (this.items.length > prevCount) {
      for (var n = prevCount; n < this.items.length; n++) {
        var tile = this.el.slots.children[n];
        if (tile) tile.classList.add('is-new');
      }
    }
    this._prevItemCount = this.items.length;
  };

  BundleBuilder.prototype.renderSummary = function () {
    var t = this.cfg.text;
    var totals = this.totals();
    this.save();
    // Card prices depend on the active tier.
    if (this._lastPct !== totals.pct) {
      this._lastPct = totals.pct;
      this.renderAllCards();
    }
    var fmt = this.cfg.moneyFormat;

    this.renderProgress(totals);
    this.renderSlots(totals);

    var minItems = this.cfg.minItems || 1;
    var minAmount = this.cfg.minAmount || 0;
    var needAmount = Math.max(0, minAmount - totals.total);
    var minLabel = formatMoney(minAmount, fmt).replace(/[.,]00(?!\d)/, '');

    if (this.el.totals) {
      var prev = this._prevTotals || {};
      this.el.totals.innerHTML =
        '<div class="bb-totals__row"><span>' + escapeHtml(t.subtotal) + '</span><span>' + formatMoney(totals.subtotal, fmt) + '</span></div>' +
        (totals.pct > 0
          ? '<div class="bb-totals__row is-discount"><span>' + escapeHtml(tpl(t.discount, { pct: totals.pct })) + '</span><span>&minus;' + formatMoney(totals.discount, fmt) + '</span></div>'
          : '') +
        (t.shippingRow
          ? '<div class="bb-totals__row is-shipping"><span>' + escapeHtml(t.shippingRow) + '</span><span>' + escapeHtml(t.shippingRowValue || 'Free') + '</span></div>'
          : '') +
        '<div class="bb-totals__row is-total"><span>' + escapeHtml(t.total) + '</span><span>' + formatMoney(totals.total, fmt) + '</span></div>';
      this._prevTotals = { pct: totals.pct, total: totals.total };
    }

    var ready = totals.count >= minItems && needAmount === 0;
    var ms = this._milestones || (this._milestones = {});
    if (ready && !ms.min) { ms.min = true; track('bb_min_reached', { items: totals.count, total: totals.total / 100 }); }
    if (totals.pct && !ms['tier' + totals.pct]) { ms['tier' + totals.pct] = true; track('bb_tier_reached', { tier_pct: totals.pct, items: totals.count, total: totals.total / 100 }); }
    var moneyShort = function (cents) { return formatMoney(cents, fmt).replace(/[.,]00(?!\d)/, ''); };
    var meterPct = minAmount ? Math.min(100, Math.round((totals.total / minAmount) * 100)) : 100;
    var ctaText;
    if (totals.count === 0) ctaText = minAmount ? tpl(t.ctaMeter, { amount: moneyShort(0), min: minLabel }) : t.ctaEmpty;
    else if (totals.count < minItems) ctaText = tpl(t.ctaMin, { count: minItems - totals.count });
    else if (needAmount > 0) ctaText = tpl(t.ctaMeter, { amount: moneyShort(totals.total), min: minLabel });
    else if (totals.discount > 0) ctaText = tpl(t.ctaReady, { amount: formatMoney(totals.discount, fmt) });
    else ctaText = t.ctaReadyNoDiscount;

    if (this.el.cta && !this.submitting) {
      if (this.cfg.checkoutEnabled === false) {
        this.el.cta.textContent = ready || totals.count === 0 ? t.ctaDisabled : ctaText;
        this.el.cta.disabled = true;
      } else {
        this.el.cta.textContent = ctaText;
        this.el.cta.disabled = !ready;
      }
      // The button doubles as a meter toward the minimum box value.
      this.el.cta.classList.toggle('is-meter', !ready && minAmount > 0);
      this.el.cta.style.setProperty('--bb-fill', meterPct + '%');
    }

    if (this.el.ctaNote) {
      var noteText = totals.count === 0 && minAmount > 0
        ? tpl(t.minAmountNote, { min: minLabel })
        : needAmount > 0
          ? tpl(t.ctaMinAmount, { amount: formatMoney(needAmount, fmt), min: minLabel })
          : t.ctaNote || '';
      this.el.ctaNote.textContent = noteText;
      this.el.ctaNote.hidden = !noteText;
    }

    var statusText =
      totals.count === 0
        ? ''
        : totals.pct > 0
          ? tpl(t.dockCountDiscount, { count: totals.count, pct: totals.pct })
          : tpl(t.dockCount, { count: totals.count });
    if (this.el.dockCount) this.el.dockCount.textContent = tpl(t.dockCount, { count: totals.count });
    if (this.el.dockCheckout) {
      this.el.dockCheckout.disabled = !ready || this.cfg.checkoutEnabled === false || !!this.submitting;
      this.el.dockCheckoutLabel.textContent = this.submitting ? t.ctaLoading : t.dockCheckout || t.ctaReadyNoDiscount;
    }
    if (this.el.dockRequirement) {
      var requirement = needAmount > 0
        ? tpl(t.dockMinAmount, { amount: moneyShort(needAmount), min: minLabel })
        : totals.count < minItems ? tpl(t.ctaMin, { count: minItems - totals.count }) : '';
      this.el.dockRequirement.textContent = requirement;
      this.el.dockRequirement.hidden = !requirement;
    }
    if (this.el.summaryNext) this.el.summaryNext.textContent = totals.next
      ? tpl(t.dockNext, { count: totals.next.qty - totals.count, pct: totals.next.pct })
      : totals.current ? tpl(t.dockMax, { pct: totals.pct }) : '';
    if (this.el.statusChip) {
      this.el.statusChip.textContent = statusText || t.chipEmpty;
      this.el.statusChip.classList.toggle('is-active', totals.count > 0);
    }
    if (this.el.dockTotal) {
      this.el.dockTotal.textContent = formatMoney(totals.total, fmt);
    }
  };

  /* ---------- cart ---------- */

  BundleBuilder.prototype.buildCartItems = function () {
    var self = this;
    var parent = this.cfg.parent;
    var bundleId = nanoid(9) + ':' + parent.productId;
    var grouped = {};
    var order = [];

    this.items.forEach(function (it) {
      var key = it.variantId + ':' + it.collectionId;
      if (!grouped[key]) {
        grouped[key] = { variantId: it.variantId, productId: it.productId, collectionId: it.collectionId, quantity: 0 };
        order.push(key);
      }
      grouped[key].quantity += 1;
    });

    var lines = order.map(function (key) {
      var g = grouped[key];
      var variant = self.variantById(g.productId, g.variantId);
      var plan = variant ? self.childPlan(variant) : null;
      var item = {
        id: g.variantId,
        quantity: g.quantity,
        properties: {
          _rc_bundle: bundleId,
          _rc_bundle_parent: parent.handle,
          _rc_bundle_variant: String(parent.variantId),
          _rc_bundle_collection_id: String(g.collectionId)
        }
      };
      if (plan && plan.id) item.selling_plan = String(plan.id);
      return item;
    });

    // Free gift: a one-time $0 line tagged _gift so the cart guard can drop it if the box is removed.
    var gift = this.cfg.gift;
    if (gift && gift.variants && gift.variants.length) {
      var chosen = this.el.giftVariant ? Number(this.el.giftVariant.value) : 0;
      var gv = gift.variants.filter(function (v) { return v.available && (!chosen || v.id === chosen); })[0] ||
        gift.variants.filter(function (v) { return v.available; })[0];
      if (gv) lines.push({ id: gv.id, quantity: 1, properties: { _gift: parent.handle } });
    }
    return lines;
  };

  BundleBuilder.prototype.submit = function () {
    var self = this;
    var t = this.cfg.text;
    if (this.submitting || this.cfg.checkoutEnabled === false) return;
    var totals = this.totals();
    if (totals.count < (this.cfg.minItems || 1)) return;
    if (totals.total < (this.cfg.minAmount || 0)) return;

    var items = this.buildCartItems();
    track('bb_checkout_click', { items: totals.count, total: totals.total / 100, tier_pct: totals.pct, subtotal: totals.subtotal / 100 });
    this.submitting = true;
    if (this.el.dockCheckout) {
      this.el.dockCheckout.disabled = true;
      this.el.dockCheckoutLabel.textContent = t.ctaLoading;
    }
    this.hideError();
    this.el.cta.classList.add('is-loading');
    this.el.cta.textContent = t.ctaLoading;

    this.setBusy(t.busyPrepare);
    this.clearPreviousBox()
      .then(function () {
        self.setBusy(t.busyAdd);
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        });
      })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || t.error);
          return data;
        });
      })
      .then(function () {
        track('bb_cart_added', { items: totals.count, total: totals.total / 100 });
        self.setBusy(t.busyGo);
        self.afterAddToCart();
      })
      .catch(function (err) {
        track('bb_cart_error', { message: String(err && err.message ? err.message : t.error).slice(0, 120) });
        self.setBusy(null);
        self.showError(err && err.message ? err.message : t.error);
        self.submitting = false;
        self.el.cta.classList.remove('is-loading');
        self.renderSummary();
      });
  };

  // Full-screen progress card shown between the checkout click and the redirect.
  BundleBuilder.prototype.setBusy = function (text) {
    if (!this.el.busy) return;
    if (!text) {
      this.el.busy.hidden = true;
      document.documentElement.classList.remove('bb-modal-lock');
      return;
    }
    if (this.el.busyText) this.el.busyText.textContent = text;
    this.el.busy.hidden = false;
    document.documentElement.classList.add('bb-modal-lock');
  };

  // A cart can only hold one TANIT Box: drop any earlier box lines (and their free gift) before
  // adding the new selection, so a rebuilt box never stacks on top of an old one.
  BundleBuilder.prototype.clearPreviousBox = function () {
    var parentHandle = this.cfg.parent.handle;
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var updates = {};
        var found = false;
        (cart.items || []).forEach(function (item) {
          var p = item.properties || {};
          var isBox = p._rc_bundle && (!p._rc_bundle_parent || p._rc_bundle_parent === parentHandle);
          if (isBox || p._gift) {
            updates[item.key] = 0;
            found = true;
          }
        });
        if (!found) return null;
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
      })
      .catch(function () { return null; });
  };

  // Third-party gift apps (BOGOS) react to the cart change asynchronously. Before leaving the page,
  // poll the cart until its item count has been stable for ~1s (max ~4s) so auto-added gifts are in.
  BundleBuilder.prototype.waitForCartToSettle = function (done) {
    if (!window.SECOMAPP && !document.querySelector('script[src*="bogos"], script[src*="secomapp"]')) return done();
    var start = Date.now();
    var last = -1;
    var stableSince = Date.now();
    function tick() {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (cart.item_count !== last) {
            last = cart.item_count;
            stableSince = Date.now();
          }
          if (Date.now() - stableSince >= 600 || Date.now() - start >= 2500) return done();
          setTimeout(tick, 250);
        })
        .catch(done);
    }
    setTimeout(tick, 300);
  };

  BundleBuilder.prototype.afterAddToCart = function () {
    if (this.afterAdd === 'checkout') {
      this.waitForCartToSettle(function () { track('bb_checkout_redirect', {}); window.location.href = '/checkout'; });
      return;
    }
    if (this.afterAdd === 'cart') {
      window.location.href = '/cart';
      return;
    }
    // Cart drawer: the theme listens for this event, rebuilds the cart and opens the drawer.
    this.setBusy(null);
    this.closeSheet();
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { bundle: true } }));
    this.items = [];
    this.submitting = false;
    this.el.cta.classList.remove('is-loading');
    this.renderAllCards();
    this.renderSummary();
  };

  BundleBuilder.prototype.showError = function (msg) {
    if (!this.el.error) return;
    this.el.error.textContent = msg;
    this.el.error.hidden = false;
    if (window.matchMedia('(max-width: 860px)').matches && !this.root.classList.contains('is-sheet-open')) this.toggleSheet();
  };

  BundleBuilder.prototype.hideError = function () {
    if (this.el.error) this.el.error.hidden = true;
  };

  function init() {
    var roots = document.querySelectorAll('[data-bundle-builder]');
    if (roots.length) document.body.classList.add('bb-active');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__bundleBuilder) return;
      root.__bundleBuilder = new BundleBuilder(root);
    });
  }

  // Coming back from checkout with the browser's Back button restores this page from the
  // back/forward cache exactly as it was left — loader up, CTA stuck on "Adding…". Reset it.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-bundle-builder]'), function (root) {
      var bb = root.__bundleBuilder;
      if (!bb || !bb.submitting) return;
      bb.setBusy(null);
      bb.submitting = false;
      bb.el.cta.classList.remove('is-loading');
      bb.renderSummary();
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', init);
})();
